import { useState, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid } from 'recharts';
import axios from 'axios';
import config from '../devprodConfig';
import LogTable from '../components/admin/LogTable';
import { getApiRequestLogs } from '../api/api';

const API_URL = config.apiBaseUrl;

const GROUPS = [
  { key: 'koho', label: 'Koho' },
  { key: 'kayttajatiedot', label: 'Käyttäjätiedot' },
  { key: 'yhteyshenkilot', label: 'Yhteyshenkilöt' },
];

const PRESETS = [
  { label: '7 pv', days: 7 },
  { label: '30 pv', days: 30 },
  { label: '90 pv', days: 90 },
  { label: 'YTD', ytd: true },
];

function isoDate(d) { return d.toISOString().slice(0, 10); }
function todayStr() { return isoDate(new Date()); }
function daysAgoStr(n) { const d = new Date(); d.setDate(d.getDate() - n); return isoDate(d); }
function yearStartStr() { const d = new Date(); d.setMonth(0, 1); return isoDate(d); }
function fmtNum(n) {
  if (!n && n !== 0) return '–';
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + ' M';
  if (n >= 1_000) return (n / 1_000).toFixed(1) + ' k';
  return String(n);
}

function getStats({ group, from, to, granularity }) {
  const params = { from, to, granularity };
  if (group) params.group = group;
  return axios.get(`${API_URL}/api/admin/request-stats`, { params, withCredentials: true });
}

const API_LOG_COLUMNS = [
  { key: 'requested_at', label: 'Aika', style: { whiteSpace: 'nowrap' }, render: r => new Date(r.requested_at).toLocaleString('fi-FI') },
  { key: 'endpoint_group', label: 'Palvelu', render: r => GROUPS.find(g => g.key === r.endpoint_group)?.label ?? r.endpoint_group },
  { key: 'method', label: 'Metodi' },
  { key: 'path', label: 'Polku', style: { maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } },
  { key: 'status_code', label: 'Status', render: r => r.status_code ? <span className={`badge ${r.status_code >= 500 ? 'bg-danger' : r.status_code >= 400 ? 'bg-warning text-dark' : 'bg-success'}`}>{r.status_code}</span> : '–' },
  { key: 'latency_ms', label: 'ms', style: { textAlign: 'right' }, render: r => r.latency_ms ?? '–' },
  { key: 'user_id', label: 'Käyttäjä', style: { maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis' } },
  { key: 'ip_address', label: 'IP' },
];

function ApiLogFilters({ values, onApply }) {
  const [v, setV] = useState(values);
  useEffect(() => { setV(values); }, [JSON.stringify(values)]); // eslint-disable-line
  const set = (k, val) => setV(p => ({ ...p, [k]: val }));
  return (
    <div className="d-flex flex-wrap gap-2 align-items-end">
      <div><label className="form-label mb-1 small">Alku</label><input type="date" className="form-control form-control-sm" value={v.from || ''} onChange={e => set('from', e.target.value)} style={{ width: 140 }} /></div>
      <div><label className="form-label mb-1 small">Loppu</label><input type="date" className="form-control form-control-sm" value={v.to || ''} onChange={e => set('to', e.target.value)} style={{ width: 140 }} /></div>
      <div><label className="form-label mb-1 small">Palvelu</label>
        <select className="form-select form-select-sm" value={v.endpoint_group || ''} onChange={e => set('endpoint_group', e.target.value)} style={{ width: 140 }}>
          <option value="">Kaikki</option>
          {GROUPS.map(g => <option key={g.key} value={g.key}>{g.label}</option>)}
        </select>
      </div>
      <div><label className="form-label mb-1 small">Metodi</label>
        <select className="form-select form-select-sm" value={v.method || ''} onChange={e => set('method', e.target.value)} style={{ width: 100 }}>
          <option value="">Kaikki</option>
          {['GET','POST','PUT','PATCH','DELETE'].map(m => <option key={m}>{m}</option>)}
        </select>
      </div>
      <div><label className="form-label mb-1 small">Status</label><input className="form-control form-control-sm" placeholder="200" value={v.status_code || ''} onChange={e => set('status_code', e.target.value)} style={{ width: 80 }} /></div>
      <div><label className="form-label mb-1 small">Polku</label><input className="form-control form-control-sm" placeholder="/api/..." value={v.path || ''} onChange={e => set('path', e.target.value)} style={{ width: 150 }} /></div>
      <div><label className="form-label mb-1 small">IP</label><input className="form-control form-control-sm" placeholder="10.0..." value={v.ip_address || ''} onChange={e => set('ip_address', e.target.value)} style={{ width: 120 }} /></div>
      <div><label className="form-label mb-1 small">Käyttäjä</label><input className="form-control form-control-sm" placeholder="id..." value={v.user_id || ''} onChange={e => set('user_id', e.target.value)} style={{ width: 120 }} /></div>
      <button className="btn btn-sm btn-success" onClick={() => onApply(v)}>Hae</button>
      <button className="btn btn-sm btn-outline-secondary" onClick={() => { const cleared = {}; setV(cleared); onApply(cleared); }}>Tyhjennä</button>
    </div>
  );
}

export default function ApiRequestStats() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Read state from URL (prefix: api_)
  const subTab    = searchParams.get('api_subtab') || 'stats';
  const urlGroup  = searchParams.get('api_group')  || '';
  const urlFrom   = searchParams.get('api_from')   || daysAgoStr(30);
  const urlTo     = searchParams.get('api_to')     || todayStr();
  const urlGran   = searchParams.get('api_gran')   || 'day';
  const urlPreset = searchParams.get('api_preset');

  // Local form state
  const [from, setFrom]               = useState(urlFrom);
  const [to, setTo]                   = useState(urlTo);
  const [granularity, setGranularity] = useState(urlGran);
  const [activePreset, setActivePreset] = useState(urlPreset ? parseInt(urlPreset, 10) : 1);

  function setUrlParams(updates) {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      Object.entries(updates).forEach(([k, v]) => {
        if (v === '' || v == null) next.delete(k);
        else next.set(k, String(v));
      });
      return next;
    }, { replace: true });
  }

  function setSubTab(v) { setUrlParams({ api_subtab: v }); }

  const load = useCallback((g, f, t, gr) => {
    setLoading(true);
    setError(null);
    getStats({ group: g || null, from: f, to: t, granularity: gr })
      .then(r => setData(r.data))
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (subTab === 'stats') load(urlGroup, urlFrom, urlTo, urlGran);
  }, [urlGroup, urlFrom, urlTo, urlGran, subTab]); // eslint-disable-line

  function applyPreset(idx) {
    const p = PRESETS[idx];
    const newFrom = p.ytd ? yearStartStr() : daysAgoStr(p.days);
    const newTo = todayStr();
    const g = p.days === 7 ? 'day' : p.ytd ? 'month' : granularity;
    setFrom(newFrom); setTo(newTo); setGranularity(g); setActivePreset(idx);
    setUrlParams({ api_from: newFrom, api_to: newTo, api_gran: g, api_preset: idx });
  }

  function applyCustom() {
    setActivePreset(null);
    setUrlParams({ api_from: from, api_to: to, api_gran: granularity, api_preset: '' });
  }

  function selectGroup(key) {
    const g = key === urlGroup ? '' : key;
    setUrlParams({ api_group: g });
  }

  const r = data?.range;

  return (
    <div>
      <ul className="nav nav-tabs mb-4">
        <li className="nav-item"><button className={`nav-link ${subTab === 'stats' ? 'active' : ''}`} onClick={() => setSubTab('stats')}>Tilastot</button></li>
        <li className="nav-item"><button className={`nav-link ${subTab === 'logs' ? 'active' : ''}`} onClick={() => setSubTab('logs')}>Lokit</button></li>
      </ul>

      {subTab === 'logs' && <LogTable fetchFn={getApiRequestLogs} columns={API_LOG_COLUMNS} filters={ApiLogFilters} paramPrefix="api_l_" />}

      {subTab === 'stats' && <>
      <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
        <span className="text-muted small me-1">Palvelu:</span>
        <button className={`btn btn-sm ${!urlGroup ? 'btn-dark' : 'btn-outline-secondary'}`} onClick={() => selectGroup(null)}>Kaikki</button>
        {GROUPS.map(g => (
          <button key={g.key} className={`btn btn-sm ${urlGroup === g.key ? 'btn-primary' : 'btn-outline-secondary'}`} onClick={() => selectGroup(g.key)}>{g.label}</button>
        ))}
      </div>

      <div className="d-flex flex-wrap align-items-center gap-2 mb-4">
        {PRESETS.map((p, i) => (
          <button key={i} className={`btn btn-sm ${activePreset === i ? 'btn-primary' : 'btn-outline-secondary'}`} onClick={() => applyPreset(i)}>{p.label}</button>
        ))}
        <div className="d-flex align-items-center gap-1 ms-2">
          <input type="date" className="form-control form-control-sm" style={{ width: 145 }} value={from} onChange={e => { setFrom(e.target.value); setActivePreset(null); }} />
          <span className="text-muted">–</span>
          <input type="date" className="form-control form-control-sm" style={{ width: 145 }} value={to} onChange={e => { setTo(e.target.value); setActivePreset(null); }} />
        </div>
        <select className="form-select form-select-sm" style={{ width: 130 }} value={granularity} onChange={e => { setGranularity(e.target.value); setActivePreset(null); }}>
          <option value="day">Päivittäin</option>
          <option value="week">Viikottain</option>
          <option value="month">Kuukausittain</option>
        </select>
        <button className="btn btn-sm btn-success" onClick={applyCustom}>Hae</button>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}
      {loading && <div className="text-muted mb-3">Ladataan...</div>}

      {data && <>
        <div className="row g-3 mb-4">
          <SCard label="Pyyntöjä yhteensä" value={fmtNum(r?.total_requests)} color="#0d6efd" />
          <SCard label="Virheelliset (≥400)" value={fmtNum(r?.error_requests)} sub={`${r?.error_rate_pct ?? 0} % virheprosentti`} color="#dc3545" />
          <SCard label="Uniikit käyttäjät" value={fmtNum(r?.unique_users)} color="#6f42c1" />
        </div>

        {data.time_series?.length > 0 && (
          <div className="card mb-4">
            <div className="card-body">
              <h6 className="card-title mb-3">Pyyynnöt aikajanalla</h6>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={data.time_series} margin={{ top: 4, right: 8, bottom: 4, left: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e9ecef" />
                  <XAxis dataKey="period" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={fmtNum} />
                  <Tooltip formatter={(v, name) => [fmtNum(v), name === 'requests' ? 'Onnistuneet' : 'Virheet']} />
                  <Legend formatter={v => v === 'requests' ? 'Onnistuneet' : 'Virheet'} />
                  <Bar dataKey="requests" stackId="a" fill="#0d6efd" />
                  <Bar dataKey="errors" stackId="a" fill="#dc3545" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        <div className="row g-3">
          {data.by_group?.length > 0 && (
            <div className="col-md-4">
              <div className="card h-100">
                <div className="card-body">
                  <h6 className="card-title mb-3">Palveluittain</h6>
                  <table className="table table-sm table-hover mb-0">
                    <thead><tr><th>Palvelu</th><th className="text-end">Pyynnöt</th><th className="text-end">Virheet</th></tr></thead>
                    <tbody>
                      {data.by_group.map(g => (
                        <tr key={g.group}>
                          <td>{GROUPS.find(x => x.key === g.group)?.label ?? g.group}</td>
                          <td className="text-end">{fmtNum(g.requests)}</td>
                          <td className="text-end text-danger">{g.errors > 0 ? fmtNum(g.errors) : '–'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {data.top_users?.length > 0 && (
            <div className="col-md-4">
              <div className="card h-100">
                <div className="card-body">
                  <h6 className="card-title mb-3">Top käyttäjät</h6>
                  <table className="table table-sm table-hover mb-0">
                    <thead><tr><th>Käyttäjä ID</th><th className="text-end">Pyynnöt</th></tr></thead>
                    <tbody>
                      {data.top_users.map(u => (
                        <tr key={u.user_id}>
                          <td style={{ fontSize: '0.8rem', fontFamily: 'monospace' }}>{u.user_id}</td>
                          <td className="text-end">{fmtNum(u.requests)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {data.by_status?.length > 0 && (
            <div className="col-md-4">
              <div className="card h-100">
                <div className="card-body">
                  <h6 className="card-title mb-3">HTTP-statukset</h6>
                  <table className="table table-sm table-hover mb-0">
                    <thead><tr><th>Status</th><th className="text-end">Pyynnöt</th></tr></thead>
                    <tbody>
                      {data.by_status.map(s => (
                        <tr key={s.status}>
                          <td><span className={`badge ${s.status >= 500 ? 'bg-danger' : s.status >= 400 ? 'bg-warning text-dark' : 'bg-success'}`}>{s.status}</span></td>
                          <td className="text-end">{fmtNum(s.requests)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>
      </>}
      </>}
    </div>
  );
}

function SCard({ label, value, sub, color }) {
  return (
    <div className="col-sm-6 col-xl-4">
      <div className="card">
        <div className="card-body">
          <div className="text-muted small mb-1">{label}</div>
          <div className="fw-bold fs-4" style={{ color }}>{value}</div>
          {sub && <div className="text-muted" style={{ fontSize: '0.78rem' }}>{sub}</div>}
        </div>
      </div>
    </div>
  );
}
